const GEO_EPS = 1e-5;
const LOCATION_NAME_MAX_LENGTH = 120;

export function findSavedLocationByGeo(locations, lat, lng, eps = GEO_EPS) {
  if (!Array.isArray(locations) || lat == null || lng == null) {
    return null;
  }
  return (
    locations.find((loc) => locationGeoMatches(loc, lat, lng, eps)) || null
  );
}

export function locationGeoMatches(loc, lat, lng, eps = GEO_EPS) {
  const la = loc?.geo?.latitude;
  const ln = loc?.geo?.longitude;
  if (typeof la !== 'number' || typeof ln !== 'number') {
    return false;
  }
  if (lat == null || lng == null) {
    return false;
  }
  return Math.abs(la - lat) < eps && Math.abs(ln - lng) < eps;
}

export function isFilledPlanPoint(point) {
  const pos = point?.point?.position;
  if (!pos) {
    return false;
  }
  const lat = pos.Latitude ?? pos.lat;
  const lng = pos.Longitude ?? pos.lng;
  if (lat == null || lng == null) {
    return false;
  }
  return !(Math.abs(lat) < 1e-9 && Math.abs(lng) < 1e-9);
}

export function formatPlanPointAddress(name, address) {
  if (name && address) {
    return `${name}, ${address}`;
  }
  return address || name || '';
}

export function buildWaypointDisplayAddress(name, address) {
  const n = (name || '').trim();
  const a = (address || '').trim();
  if (n && a && n.toLowerCase() === a.toLowerCase()) {
    return a;
  }
  return formatPlanPointAddress(n, a);
}

export function normalizeLocationName(label) {
  if (!label || typeof label !== 'string') {
    return '';
  }
  const trimmed = label.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.length <= LOCATION_NAME_MAX_LENGTH) {
    return trimmed;
  }
  return trimmed.slice(0, LOCATION_NAME_MAX_LENGTH).trim();
}

function mapPickResultFromSaved(existing) {
  return {
    name: existing.name,
    address: existing.address || existing.name || '',
    locationId: existing.id ?? null,
    pendingCreate: false,
    postcode: existing.postcode ?? null,
  };
}

/**
 * Resolve saved vs new location for map context-menu pick.
 * Coordinates take priority; new names derive from full geocode label.
 */
export async function resolveMapPickLocation({
  lat,
  lng,
  addressLabel,
  savedLocations,
  getLocationByName,
}) {
  const existingByGeo = findSavedLocationByGeo(savedLocations, lat, lng);
  if (existingByGeo) {
    return mapPickResultFromSaved(existingByGeo);
  }

  const baseName = normalizeLocationName(addressLabel);
  if (!baseName) {
    const fallback = `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
    return {
      name: fallback,
      address: fallback,
      locationId: null,
      pendingCreate: true,
      postcode: null,
    };
  }

  const rawAddress = (addressLabel || baseName).trim();
  let candidate = baseName;
  let suffix = 2;
  const maxAttempts = 200;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const existing = await getLocationByName(candidate);
    if (!existing) {
      return {
        name: candidate,
        address: rawAddress,
        locationId: null,
        pendingCreate: true,
        postcode: null,
      };
    }
    if (locationGeoMatches(existing, lat, lng)) {
      return mapPickResultFromSaved(existing);
    }
    candidate = `${baseName} ${suffix}`;
    suffix += 1;
  }

  return {
    name: `${baseName} ${Date.now()}`,
    address: rawAddress,
    locationId: null,
    pendingCreate: true,
    postcode: null,
  };
}
